import { useLocation } from 'react-router-dom'
import { ActiveAccountContext } from '@signal/core/user/Context'
import { getClientDomain } from '@signal/utils/env/apiDomain'
import { useEffect, useContext } from 'react'
import UI_BASE_PATH from '@signal/core/baseline/app/base-path'
import { AppRouteType } from '@signal/core/baseline/app/types'
// @ts-ignore
import { createRoute } from '@signal/router'

export const ACCOUNT_ID_REDIR_SUBST = '#{Account.Guid}'

export const getAcctSelectorUrl = (
  defaultTargetPath: string,
  // TODO: remove support for unencoded redirect once we pushed all FE apps
  redirect?: string,
  encodedRedirect?: string,
  activeAccountId?: string
): string => {
  if (!redirect && !encodedRedirect) {
    redirect = `https://${window.location.host}${UI_BASE_PATH}${defaultTargetPath}`
  }
  // use https://dev.local.purecars.com:3099 instead of ${getClientDomain()}/sheriff/account-selector if debugging
  // the account selector app locally
  const url = `${getClientDomain()}/sheriff/account-selector`
  const redirectQP = `?redirect_encoded=${encodedRedirect ||
    encodeURIComponent(btoa(redirect!))}`
  const accountIdQP = activeAccountId
    ? `&activeAccountId=${activeAccountId}`
    : ''
  return url + redirectQP + accountIdQP
}

/**
 * This hook is used to replace an account id within your url with the account guid substitution code.
 * This will target you to your account selector route set up to be redirected to where you were after
 * switching accounts.
 * @param {string} localAccountSelectionPath - the path to your application's account selector, ususally '/'
 * @param {Function} modifyPath - if you may need to modify the path in a way beyond subtituting the accountId,
 *   you can do so in this callback
 * @return {string} - the URL to use for your local account selection page, with a redirect that will be used by
 *   the sheriff's account selection app.
 */
export const useDynamicPathReplacement = (
  localAccountSelectionPath: string | Array<string> = '/',
  modifyPath?: (path: string) => string
): string => {
  const { pathname, search, hash } = useLocation()
  const { activeAccount } = useContext(ActiveAccountContext)

  // if an array of possible account selection paths is provided, choose the one which matches the current path
  if (Array.isArray(localAccountSelectionPath)) {
    localAccountSelectionPath =
      // exclude '/' to avoid premature matching in find, if we don't match to anything else we will use that ultimately
      localAccountSelectionPath.find(
        path => path !== '/' && pathname.includes(path)
      ) || '/'
  }

  if (!activeAccount) {
    return localAccountSelectionPath
  }

  let pathnameUse = pathname.replace(
    activeAccount.accountId,
    ACCOUNT_ID_REDIR_SUBST
  )

  if (!pathnameUse.includes('Account.Guid')) {
    // If it has no dashes, also replace the dashless account ID
    pathnameUse = pathname.replace(
      activeAccount.accountId.replaceAll('-', ''),
      ACCOUNT_ID_REDIR_SUBST
    )
  }

  let newUrl = `${window.location.origin}${UI_BASE_PATH}${pathnameUse}${search}${hash}`
  if (modifyPath) {
    newUrl = modifyPath(newUrl)
  }

  return `${localAccountSelectionPath}?redirect_encoded=${encodeURIComponent(
    btoa(newUrl)
  )}&activeAccountId=${activeAccount.accountId}`
}

/**
 * This hook is used to get a standard dynamic path replacement hook based on the current location and set of routes
 * @param {Array} routes - the array of routes for the app
 * @return {String} - the path for account selection appropriate for the current location in the app
 */
export const useStandardDynamicPathReplacementHook = (
  routes: Array<AppRouteType>
) => {
  const { pathname } = useLocation()
  const routeMatch = routes.find(route =>
    createRoute(route.path).test(pathname)
  )
  const localAccountSelectionPath =
    (routeMatch && routeMatch.accountSelectionPath) || '/'
  return useDynamicPathReplacement(localAccountSelectionPath)
}

/**
 * This hook is used to handle the back and forth redirect to the account selector page,
 * you need to supply the target path in order to be correctly redirected after selecting an account.
 * Dynamic routes that expects an id are usually built like https://myDomain/:accountId/*
 * The replacement function expects the dynamic param with # prefix following by the replacement id within curly braces,
 * so if you have an route like the mentioned above, the adjusted path would be:
 * https://myDomain/#{Account.Guid} or...
 * https://myDomain/#{Account.Guid}/any/further/path
 * @param {string} defaultTargetPath - target path after selecting an account(should contain #{Account.Guid})
 */
export const useAccountSelectionRedirect = (
  defaultTargetPath: string
): null => {
  const location = useLocation()
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const activeAccountIdQP = params.get('activeAccountId') || undefined
    // TODO: remove support for unencoded redirect once we pushed all FE apps
    const redirectQP = params.get('redirect') || undefined
    const encodedRedirectQP = params.get('redirect_encoded') || undefined

    window.location.replace(
      getAcctSelectorUrl(
        defaultTargetPath,
        redirectQP,
        encodedRedirectQP,
        activeAccountIdQP
      )
    )
  }, [location, defaultTargetPath])

  return null
}

export default useAccountSelectionRedirect
